import { msg } from "@lingui/core/macro";
import { createEffect, onCleanup, onMount, Show } from "solid-js";
import { Outlet, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { appState, useInteractionFlag } from "@fleet/ui";
import { useSession } from "@/lib/auth";
import { resetUndoScope, undoLastAction } from "@/lib/undo";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useI18n } from "@/providers/i18n-provider";
import { useTheme } from "@/providers/theme-provider";

import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ContentRegionSkeleton } from "@/components/workspace-skeleton";

import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { useUpcoming } from "@/features/reminders/hooks/use-upcoming";

interface AppLayoutContentProps {
  /**
   * PowerSync database, or `null` while its local SQLite is still
   * initializing. The static shell renders regardless; data-bound regions
   * show granular skeletons until this is non-null and their queries settle.
   */
  db: AbstractPowerSyncDatabase | null;
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking is owned by <InteractionProvider>;
  // only the sidebar-specific Escape handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  // Entry-animation key: the *leaf* routeId, NOT the pathname. The swipe
  // carousel between assets changes route params (and thus the pathname) while
  // staying on the same route — keying by routeId means the carousel surface
  // owns its own transform and the entry animation does not re-fire or fight it.
  const leafRouteId = () => routerState().matches.at(-1)?.routeId;

  // The local DB is ready once PowerSync's SQLite has initialized.
  const isDbReady = () => Boolean(props.db);

  const theme = useTheme();
  const { t } = useI18n();
  const upcoming = useUpcoming();

  // Sidebar summary counts depend on the local DB + their own queries.
  const areCountsLoading = () => !isDbReady() || upcoming.isLoading();

  const { isSidebarOpen, setIsSidebarOpen, shortcutsOpen, setShortcutsOpen } = useAppDialogs();

  useAppShortcuts({
    theme,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  // ── Undo (L1) ──────────────────────────────────────────────────────────
  // Ctrl/Cmd+Z outside inputs/dialogs (the `when` lives in defaults.ts). The
  // stack is per-account: when the active account changes (multi-account/login/logout)
  // it is cleared — an undo must NEVER run against another session.
  const session = useSession();
  createEffect(() => {
    resetUndoScope(session.userId() || undefined);
  });
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLastAction();
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). There is no page-level swipe gesture in this repo yet to stand
  // down, but the flag is the declaration of ownership regardless — the day
  // one is added, it just works.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      background={theme.tokens()?.background}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
    >
      <AppLayoutSidebar
        isOpen={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        dueCount={upcoming.dueCount}
        countsLoading={areCountsLoading}
      />

      {/* Skip-to-content: first focusable element, visible only on keyboard focus. */}
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-focus/50"
      >
        {t(msg`Skip to content`)}
      </a>

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] focus:outline-none"
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <div class="flex flex-1 flex-col min-h-0 h-full">
            {/* Content region: granular skeleton until the local DB is ready,
                then the routed screen renders its own data. */}
            <Show when={isDbReady()} fallback={<ContentRegionSkeleton />}>
              {/* Entry animation lives ONCE here, at the Outlet level, keyed by
                  the leaf routeId so it re-fires per screen change (but not on
                  carousel param swaps). GPU-only (transform + opacity),
                  motion-safe so reduced-motion users get an instant render.
                  This is a *separate* wrapper from any inner transform surface
                  (e.g. the asset carousel), so the two never compete. */}
              <Show when={leafRouteId()} keyed fallback={<Outlet />}>
                <div class="flex flex-1 flex-col min-h-0 h-full motion-safe:animate-slide-in-up motion-safe:will-change-[transform,opacity]">
                  <Outlet />
                </div>
              </Show>
            </Show>
          </div>
        </div>
      </main>

      <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen} />
    </AppBackground>
  );
}
